import React from 'react';
import { motion } from 'framer-motion';

const ZONE_KEYWORDS = {
  bedroom: ['chambre', 'lit', 'dressing', 'coucher', 'dormir'],
  living: ['salon', 'séjour', 'living', 'canapé', 'télé', 'tv'],
  bathroom: ['salle de bain', 'sdb', 'douche', 'baignoire', 'wc', 'toilette', 'lavabo'],
  kitchen: ['cuisine', 'cuisiniste', 'évier', 'plan de travail', 'crédence', 'hotte'],
  dining: ['salle à manger', 'table', 'chaises', 'repas'],
  stairs: ['escalier', 'marche', 'étage'],
  floor: ['sol', 'carrelage', 'parquet', 'plancher', 'revêtement'],
  walls: ['peinture', 'mur', 'murs', 'enduit', 'papier peint', 'cloison'],
  electrical: ['électricité', 'électrique', 'éclairage', 'prise'],
  plumbing: ['plomberie', 'tuyauterie', 'canalisation'],
  windows: ['fenêtre', 'fenêtres', 'porte', 'portes', 'menuiserie', 'baie vitrée'],
};

const isZoneActive = (description, zone) => {
  if (!description) return false;
  const lowerDesc = description.toLowerCase();
  return ZONE_KEYWORDS[zone]?.some(keyword => lowerDesc.includes(keyword)) || false;
};

const InteractiveHouse = ({ description = '' }) => {
  const z = {
    bedroom: isZoneActive(description, 'bedroom'),
    living: isZoneActive(description, 'living'),
    bathroom: isZoneActive(description, 'bathroom'),
    kitchen: isZoneActive(description, 'kitchen'),
    dining: isZoneActive(description, 'dining'),
    stairs: isZoneActive(description, 'stairs'),
    floor: isZoneActive(description, 'floor'),
    walls: isZoneActive(description, 'walls'),
    electrical: isZoneActive(description, 'electrical'),
    plumbing: isZoneActive(description, 'plumbing'),
    windows: isZoneActive(description, 'windows'),
  };

  const activeCount = Object.values(z).filter(Boolean).length;

  // Zone positions on the image (in percentages)
  const zones = [
    { key: 'bedroom', label: 'Chambre', x: 48, y: 28 },
    { key: 'bathroom', label: 'Salle de bain', x: 75, y: 32 },
    { key: 'living', label: 'Salon', x: 25, y: 75 },
    { key: 'kitchen', label: 'Cuisine', x: 82, y: 68 },
    { key: 'dining', label: 'Salle à manger', x: 55, y: 72 },
    { key: 'stairs', label: 'Escalier', x: 38, y: 52 },
    { key: 'walls', label: 'Murs', x: 15, y: 35 },
    { key: 'floor', label: 'Sol', x: 50, y: 85 },
    { key: 'windows', label: 'Fenêtres', x: 12, y: 55 },
    { key: 'electrical', label: 'Électricité', x: 30, y: 25 },
    { key: 'plumbing', label: 'Plomberie', x: 70, y: 45 },
  ];

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-4">
        <h3 className="text-lg font-bold text-foreground">Votre projet</h3>
        <p className="text-sm text-muted-foreground">
          {activeCount > 0 ? `${activeCount} zone${activeCount > 1 ? 's' : ''} identifiée${activeCount > 1 ? 's' : ''}` : 'Décrivez vos travaux'}
        </p>
      </motion.div>

      {/* Image container with markers */}
      <div className="relative w-full max-w-xl">
        <img 
          src="https://customer-assets.emergentagent.com/job_ed87342b-4ae2-4da8-92b0-8610cc6caf88/artifacts/4byb7x7q_IMG_8373.jpeg"
          alt="Plan 3D maison"
          className="w-full h-auto rounded-xl shadow-lg"
        />
        
        {/* Orange markers for active zones */}
        {zones.map(({ key, label, x, y }) => (
          z[key] && (
            <motion.div
              key={key}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="absolute"
              style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
            >
              {/* Pulsing ring */}
              <motion.div
                className="absolute w-12 h-12 rounded-full bg-amber-500/30"
                style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              />
              {/* Main dot */}
              <div className="w-6 h-6 rounded-full bg-amber-500 shadow-lg shadow-amber-500/50 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-white" />
              </div>
              {/* Label */}
              <div className="absolute top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-amber-500 text-white text-xs font-medium px-2 py-1 rounded shadow-md">
                {label}
              </div>
            </motion.div>
          )
        ))}
      </div>

      {/* Legend */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.3 }}
        className="mt-5 flex flex-wrap justify-center gap-1.5 max-w-xl"
      >
        {zones.map(({ key, label }) => (
          <span 
            key={key}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all
              ${z[key] ? 'bg-amber-500 text-white shadow-md' : 'bg-slate-200 text-slate-400 dark:bg-slate-700'}`}
          >
            {label}
          </span>
        ))}
      </motion.div>
    </div>
  );
};

export default InteractiveHouse;
